import { Injectable, Inject } from '@angular/core';
import { TRANSLATIONS } from './translations';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class TranslateService {
  private currentLang: string;
  private defaultLang = 'EN';
  private fallback: boolean;
  private PLACEHOLDER = '%';

  onLangChanged: BehaviorSubject<string> = new BehaviorSubject(this.defaultLang);

  constructor(
    @Inject(TRANSLATIONS) private translations: any
  ) {}

  getCurrentLang(): string {
    return this.currentLang || this.defaultLang;
  }

  setDefaultLang(lang: string): void {
    this.defaultLang = lang;
  }

  enableFallback(enable: boolean): void {
    this.fallback = enable;
  }

  use(lang: string): void {
    this.currentLang = lang;
    this.onLangChanged.next(lang);
  }

  /*
  * translate: <string>
  *
  * Takes in a key<string> that consists of either a single word (e.g. 'HELLO'),
  * or multiple words separated by dots (e.g. 'NESTED.KEY'). Adding dots to the
  * key string will allow for nested dictionary object translation retrieval.
  *
  * Returns a <string> containing either the translation corresponding with the key,
  * or the key itself if no translation is found.
  *
  * 'NESTED.KEY' corresponds with the following translation:
  * {
  *   NESTED: {
  *     KEY: 'translation'
  *   }
  * }
  */
  translate(key: string): string {
    const translateKey: string[] = key.split('.');
    let translation;

    if (this.currentLang) {
      translation = JSON.parse(JSON.stringify(this.translations))[this.currentLang];
    } else if (this.fallback && this.defaultLang) {
      translation = JSON.parse(JSON.stringify(this.translations))[this.defaultLang];
    } else {
      return key;
    }

    for (let i = 0; i < translateKey.length; i++) {
      if (!translation || !translation.hasOwnProperty(translateKey[i])) {
        return key;
      }
      translation = translation[translateKey[i]];
    }

    return translation;
  }

  /*
  * replace: <string>
  *
  * Takes a translation and replaces its placeholders with any specified words<string | string[]>.
  *
  * replace('Hello %0, my name is %1.', ['Alice', 'Bob']) outputs 'Hello Alice, my name is Bob'
  */
  replace(word: string = '', words: string | string[] = ''): string {
    let translation: string = word;

    const values: string[] = [].concat(words);
    values.forEach((e, i) => {
      translation = translation.replace(this.PLACEHOLDER.concat(<any>i), e);
    })

    return translation;
  }

  /*
  * instant: <string>
  *
  * Takes in a dictionary key<string> with any number of placeholder words<string | string[]>,
  * and returns the desired translation, if found. Otherwise, returns the original key.
  */
  instant(key: string = '', words?: string | string[]): string {
    if (!key) return key;

    const translation: string = this.translate(key);

    return !words ? translation : this.replace(translation, words);
  }
}



// WEBPACK FOOTER //
// /Users/jlam2/impresocloud/www/client/src/app/i18n/translate.service.ts